/**
 * EventCloseoutWorkspace — UEIM-06
 * Post-event operational closure workspace.
 * Route: /crm/events/:eid/closeout
 *
 * 6 tabs: Overview | Delivery | Partners | Team & Streams | Client Feedback | Lessons
 *
 * Architectural invariants:
 * - Event must be COMPLETED before closeout can begin
 * - UEIM-04 scope baseline is READ-ONLY (never mutated)
 * - UEIM-05 operational records are READ-ONLY evidence
 * - No Finance, No AI, No HCM
 * - COMPLETED→CLOSED only via UEIM-06 closeout gate
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, CheckSquare, Loader2, Plus, RefreshCw, X,
  ClipboardList, Users, Star, BookOpen, CheckCircle2,
  AlertTriangle, Building2, Layers, MessageSquare, ChevronDown,
  AlertCircle, Pencil, Trash2,
} from "lucide-react";
import { closeoutApi, eventApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
const Stars = ({ value, max = 5 }) => (
  <span className="text-amber-400">{"★".repeat(value)}{"☆".repeat(max - value)}</span>
);

const GATE_STYLE = {
  READY:                "bg-emerald-100 text-emerald-800 border-emerald-300",
  READY_WITH_EXCEPTIONS:"bg-amber-100 text-amber-800 border-amber-300",
  NOT_READY:            "bg-red-100 text-red-800 border-red-300",
  NOT_EVALUATED:        "bg-slate-100 text-slate-500 border-slate-200",
};
const DELIVERY_STATUS_STYLE = {
  PENDING:            "bg-slate-100 text-slate-500",
  DELIVERED:          "bg-emerald-100 text-emerald-700",
  PARTIALLY_DELIVERED:"bg-amber-100 text-amber-700",
  NOT_DELIVERED:      "bg-red-100 text-red-700 font-semibold",
  NOT_APPLICABLE:     "bg-slate-100 text-slate-400",
};
const STREAM_OUTCOME_STYLE = {
  SUCCESSFUL:              "bg-emerald-100 text-emerald-700",
  SUCCESSFUL_WITH_ISSUES:  "bg-amber-100 text-amber-700",
  PARTIAL:                 "bg-orange-100 text-orange-700",
  FAILED:                  "bg-red-100 text-red-700 font-semibold",
  NOT_APPLICABLE:          "bg-slate-100 text-slate-400",
};
const IMPACT_STYLE = {
  LOW: "bg-slate-100 text-slate-500",
  MEDIUM: "bg-amber-100 text-amber-700",
  HIGH: "bg-red-100 text-red-700",
};
const LESSON_CATEGORIES = [
  "DESIGN","SOURCING","PARTNER","VENUE","LOGISTICS",
  "AV","CATERING","TEAM","CLIENT","SCHEDULE","OPERATIONS","OTHER"
];

const TABS = [
  { id: "overview",   label: "Overview",       icon: ClipboardList },
  { id: "delivery",   label: "Delivery",        icon: CheckSquare },
  { id: "partners",   label: "Partners",        icon: Building2 },
  { id: "streams",    label: "Team & Streams",  icon: Layers },
  { id: "feedback",   label: "Client Feedback", icon: MessageSquare },
  { id: "lessons",    label: "Lessons",         icon: BookOpen },
];

// ── Root ─────────────────────────────────────────────────────────────────────
export default function EventCloseoutWorkspace() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const canRead           = hasCapability(user, "event.closeout.read");
  const canManage         = hasCapability(user, "event.closeout.manage");
  const canDelivery       = hasCapability(user, "event.closeout.delivery.verify");
  const canSupplier       = hasCapability(user, "event.closeout.supplier.review");
  const canStream         = hasCapability(user, "event.closeout.stream.review");
  const canFeedback       = hasCapability(user, "event.closeout.feedback.manage");
  const canLesson         = hasCapability(user, "event.closeout.lesson.manage");
  const canEvaluate       = hasCapability(user, "event.closeout.evaluate");
  const canClose          = hasCapability(user, "event.closeout.close");

  const loadEvent = useCallback(async () => {
    try { const r = await eventApi.getEvent(eid); setEvent(r.data); }
    catch { toast.error("Failed to load event"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!event) return <div className="min-h-screen flex items-center justify-center text-slate-500">Event not found.</div>;
  if (!canRead) return <div className="min-h-screen flex items-center justify-center text-slate-500">You do not have access to Event Closeout.</div>;

  const EVENT_STATUS_STYLE = {
    COMPLETED: "bg-blue-100 text-blue-700",
    CLOSED: "bg-slate-100 text-slate-500",
    LIVE: "bg-emerald-100 text-emerald-700",
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(`/crm/events/${eid}`)} className="text-slate-400 hover:text-slate-600" data-testid="back-to-event-closeout-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-blue-600" />
                  <h1 className="font-bold text-slate-900 text-xl">{event.event_name}</h1>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${EVENT_STATUS_STYLE[event.status] || "bg-slate-100 text-slate-500"}`}>{event.status}</span>
                  <span className="text-xs text-slate-400 font-medium uppercase tracking-wide ml-1">Event Closeout</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{event.primary_venue} · {fmtDate(event.event_start_at)}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-1 mt-4 overflow-x-auto pb-0.5">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`closeout-tab-${t.id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${active ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"}`}>
                  <Icon className="w-3.5 h-3.5" />{t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {tab === "overview"  && <OverviewTab eid={eid} event={event} canManage={canManage} canEvaluate={canEvaluate} canClose={canClose} onEventUpdate={loadEvent} />}
        {tab === "delivery"  && <DeliveryTab eid={eid} canDelivery={canDelivery} />}
        {tab === "partners"  && <PartnersTab eid={eid} canSupplier={canSupplier} />}
        {tab === "streams"   && <StreamsTab eid={eid} canStream={canStream} />}
        {tab === "feedback"  && <FeedbackTab eid={eid} canFeedback={canFeedback} />}
        {tab === "lessons"   && <LessonsTab eid={eid} canLesson={canLesson} />}
      </div>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ eid, event, canManage, canEvaluate, canClose, onEventUpdate }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [sessionForm, setSessionForm] = useState({ closeout_owner_user_id: "" });
  const [creating, setCreating] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeNotes, setCloseNotes] = useState("");
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const load = useCallback(async () => {
    try { const r = await closeoutApi.getOverview(eid); setOverview(r.data); }
    catch { toast.error("Failed to load closeout overview"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const activateSession = async (sess) => {
    try {
      await closeoutApi.transitionSession(eid, sess.id, { target_status: "OPEN" });
      toast.success("Closeout session opened");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const advanceSession = async (sess, target) => {
    try {
      await closeoutApi.transitionSession(eid, sess.id, { target_status: target });
      toast.success(`Session → ${target}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await closeoutApi.createSession(eid, sessionForm);
      toast.success("Closeout session created");
      setShowCreateSession(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const evaluateGate = async () => {
    setEvaluating(true);
    try {
      const r = await closeoutApi.evaluateGate(eid);
      toast.success(`Gate: ${r.data.gate_status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gate evaluation failed"); }
    finally { setEvaluating(false); }
  };

  const handleCloseEvent = async () => {
    setClosing(true);
    try {
      await closeoutApi.closeEvent(eid, { notes: closeNotes });
      toast.success("Event is now CLOSED");
      setShowCloseConfirm(false);
      onEventUpdate();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Close failed"); }
    finally { setClosing(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!overview) return null;

  const sess = overview.active_session;
  const gate = overview.gate_status;
  const stale = overview.gate_stale;
  const canCloseNow = canClose && event.status === "COMPLETED" && sess?.status === "COMPLETED" && (gate === "READY" || gate === "READY_WITH_EXCEPTIONS") && !stale;
  const SESSION_NEXT = { NOT_STARTED: "OPEN", OPEN: "REVIEWING", REVIEWING: "READY_TO_CLOSE", READY_TO_CLOSE: "COMPLETED" };

  return (
    <div className="space-y-6">
      {/* Session + Gate row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Session card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-blue-500" />Closeout Session</h3>
            {canManage && !sess && event.status === "COMPLETED" && (
              <Button size="sm" variant="outline" onClick={() => setShowCreateSession(true)} data-testid="create-closeout-session-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Open Closeout
              </Button>
            )}
            {canManage && sess && sess.status !== "COMPLETED" && sess.status !== "CLOSED" && SESSION_NEXT[sess.status] && (
              <Button size="sm" variant="outline" onClick={() => advanceSession(sess, SESSION_NEXT[sess.status])} data-testid="advance-session-btn">
                → {SESSION_NEXT[sess.status]}
              </Button>
            )}
          </div>
          {event.status !== "COMPLETED" && event.status !== "CLOSED" && (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
              Event must be COMPLETED before closeout can begin.
            </p>
          )}
          {sess ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">Session #{sess.id?.slice(-6)}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sess.status === "OPEN" || sess.status === "REVIEWING" || sess.status === "READY_TO_CLOSE" ? "bg-blue-100 text-blue-700" : sess.status === "COMPLETED" || sess.status === "CLOSED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{sess.status}</span>
              </div>
              {sess.opened_at && <p className="text-xs text-slate-500">Opened: {fmt(sess.opened_at)}</p>}
              {sess.closeout_owner_user_id && <p className="text-xs text-slate-500">Owner: {sess.closeout_owner_user_id}</p>}
              <p className="text-xs text-slate-400 mt-1">State version: {sess.closeout_state_version}</p>
            </div>
          ) : event.status === "COMPLETED" ? (
            <p className="text-sm text-slate-400">No closeout session yet. {canManage ? "Create one above." : ""}</p>
          ) : null}

          {showCreateSession && (
            <form onSubmit={handleCreateSession} className="mt-4 border-t border-slate-100 pt-4 space-y-3">
              <div>
                <Label className="text-xs">Closeout Owner (User ID) *</Label>
                <Input placeholder="user ID" value={sessionForm.closeout_owner_user_id}
                  onChange={e => setSessionForm(p => ({...p, closeout_owner_user_id: e.target.value}))}
                  required className="mt-1 h-8 text-sm" data-testid="closeout-owner-input" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={creating} data-testid="closeout-session-submit">{creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Create</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowCreateSession(false)}>Cancel</Button>
              </div>
            </form>
          )}
        </div>

        {/* Gate card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-indigo-500" />Closeout Gate</h3>
            <div className="flex gap-2">
              {canEvaluate && sess && !["CLOSED"].includes(sess.status) && (
                <Button size="sm" variant="outline" onClick={evaluateGate} disabled={evaluating} data-testid="evaluate-gate-btn">
                  {evaluating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Evaluate
                </Button>
              )}
              {canCloseNow && (
                <Button size="sm" className="bg-slate-800 hover:bg-slate-900 text-white" onClick={() => setShowCloseConfirm(true)} data-testid="close-event-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Close Event
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${GATE_STYLE[gate] || GATE_STYLE.NOT_EVALUATED}`} data-testid="closeout-gate-badge">{gate?.replace(/_/g, " ")}</span>
            {stale && (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="closeout-gate-stale-badge">
                <AlertCircle className="w-3 h-3" />Stale — re-evaluate
              </span>
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div>Scope verified: <span className="font-medium">{overview.delivery.delivered + overview.delivery.partial}/{overview.delivery.total}</span></div>
            {overview.delivery.pending > 0 && <div className="text-amber-600">Pending: {overview.delivery.pending}</div>}
            <div>Critical incidents open: <span className={`font-medium ${overview.open_critical_incidents > 0 ? "text-red-600" : ""}`}>{overview.open_critical_incidents}</span></div>
            <div>Partners reviewed: <span className="font-medium">{overview.supplier_reviews.submitted}/{overview.supplier_reviews.total}</span></div>
          </div>
        </div>
      </div>

      {/* Close Event confirmation modal */}
      {showCloseConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h4 className="font-semibold text-slate-800 mb-3">Close Event</h4>
            <p className="text-sm text-slate-600 mb-3">This will permanently close the event. Gate is <strong>{gate}</strong>. This action is irreversible.</p>
            <Textarea placeholder="Closing notes (optional)" value={closeNotes} onChange={e => setCloseNotes(e.target.value)} rows={3} className="text-sm mb-4" data-testid="close-notes-input" />
            <div className="flex gap-2">
              <Button size="sm" className="bg-slate-800 hover:bg-slate-900 text-white" onClick={handleCloseEvent} disabled={closing} data-testid="close-event-confirm-btn">{closing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Confirm Close</Button>
              <Button size="sm" variant="outline" onClick={() => setShowCloseConfirm(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Attention Required */}
      {overview.attention_required?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Attention Required</h4>
          <div className="flex flex-wrap gap-2">
            {overview.attention_required.map((a, i) => (
              <span key={i} className="text-xs bg-white border border-amber-300 text-amber-800 px-2.5 py-1 rounded-full" data-testid={`closeout-attention-${i}`}>{a.label}</span>
            ))}
          </div>
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Delivered",  ok: overview.delivery.delivered,  total: overview.delivery.total,               warn: overview.delivery.not_delivered, icon: CheckSquare,   c: "text-emerald-600 bg-emerald-50",  testid: "tile-delivery" },
          { label: "Partial",    ok: overview.delivery.partial,    total: overview.delivery.total,               warn: 0, icon: AlertCircle,  c: "text-amber-600 bg-amber-50",     testid: "tile-partial" },
          { label: "Partner Rev",ok: overview.supplier_reviews.submitted, total: overview.supplier_reviews.total, warn: 0, icon: Building2,   c: "text-purple-600 bg-purple-50",   testid: "tile-partner-reviews" },
          { label: "Stream Rev", ok: overview.stream_reviews.submitted, total: overview.stream_reviews.total,    warn: 0, icon: Layers,       c: "text-indigo-600 bg-indigo-50",   testid: "tile-stream-reviews" },
          { label: "Feedback",   ok: overview.client_feedback_status === "RECEIVED" ? 1 : 0, total: 1, warn: 0, icon: MessageSquare, c: overview.client_feedback_status === "RECEIVED" ? "text-emerald-600 bg-emerald-50" : "text-slate-600 bg-slate-50", testid: "tile-feedback" },
          { label: "Lessons",    ok: overview.lessons_count,       total: overview.lessons_count,                warn: 0, icon: BookOpen,     c: "text-teal-600 bg-teal-50",       testid: "tile-lessons" },
        ].map(({ label, ok, total, warn, icon: Icon, c, testid }) => (
          <div key={label} data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-2 ${c}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="text-lg font-bold text-slate-900">{ok}/{total || "—"}</div>
            <div className="text-xs text-slate-500">{label}</div>
            {warn > 0 && <div className="text-xs text-red-500 mt-0.5">{warn} issues</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Delivery Tab ──────────────────────────────────────────────────────────────
function DeliveryTab({ eid, canDelivery }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ verification_status: "DELIVERED", delivered_quantity: "", delivery_note: "", exception_reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await closeoutApi.listDelivery(eid); setItems(r.data); }
    catch { toast.error("Failed to load delivery verifications"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      verification_status: item.verification_status || "DELIVERED",
      delivered_quantity: item.delivered_quantity ?? "",
      delivery_note: item.delivery_note || "",
      exception_reason: item.exception_reason || "",
    });
  };

  const handleSave = async (vid) => {
    setSaving(true);
    try {
      const payload = { verification_status: form.verification_status };
      if (form.delivered_quantity !== "") payload.delivered_quantity = Number(form.delivered_quantity);
      if (form.delivery_note) payload.delivery_note = form.delivery_note;
      if (form.exception_reason) payload.exception_reason = form.exception_reason;
      await closeoutApi.updateDelivery(eid, vid, payload);
      toast.success("Delivery verification saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Scope Delivery Verification ({items.length} lines)</h3>
        <p className="text-xs text-slate-400 italic">Committed scope values are read-only — from planning baseline</p>
      </div>
      {items.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-slate-400 text-sm">No scope lines found for this event.</p>
          <p className="text-slate-400 text-xs mt-1">Open a closeout session first to initialize delivery verifications.</p>
        </div>
      )}
      <div className="space-y-3">
        {items.map(item => (
          <div key={item.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`delivery-row-${item.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div className="flex-1">
                <p className="font-medium text-slate-800 text-sm">{item.scope_line_description || "Unnamed scope line"}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Committed: <span className="font-mono font-medium">{item.committed_quantity ?? "—"} {item.committed_unit}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DELIVERY_STATUS_STYLE[item.verification_status] || ""}`} data-testid={`delivery-status-${item.id}`}>{item.verification_status}</span>
                {canDelivery && item.verification_status === "PENDING" && (
                  <button onClick={() => openEdit(item)} className="text-xs px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid={`delivery-edit-${item.id}`}>
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
                {canDelivery && item.verification_status !== "PENDING" && (
                  <button onClick={() => openEdit(item)} className="text-xs px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid={`delivery-edit-done-${item.id}`}>
                    Edit
                  </button>
                )}
              </div>
            </div>

            {/* Inline edit form */}
            {editing === item.id && canDelivery && (
              <div className="mt-4 border-t border-slate-100 pt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Verification Status *</Label>
                    <select value={form.verification_status} onChange={e => setForm(p => ({...p, verification_status: e.target.value}))}
                      className="mt-1 h-8 text-xs w-full border border-slate-200 rounded-md px-2" data-testid={`delivery-status-select-${item.id}`}>
                      {["DELIVERED","PARTIALLY_DELIVERED","NOT_DELIVERED","NOT_APPLICABLE"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Delivered Quantity</Label>
                    <Input type="number" placeholder="qty" value={form.delivered_quantity} onChange={e => setForm(p => ({...p, delivered_quantity: e.target.value}))}
                      className="mt-1 h-8 text-xs" data-testid={`delivery-qty-${item.id}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Delivery Note</Label>
                    <Input placeholder="What was actually delivered" value={form.delivery_note} onChange={e => setForm(p => ({...p, delivery_note: e.target.value}))}
                      className="mt-1 h-8 text-xs" data-testid={`delivery-note-${item.id}`} />
                  </div>
                  {form.verification_status === "NOT_DELIVERED" && (
                    <div className="col-span-2">
                      <Label className="text-xs">Exception Reason * (required for NOT_DELIVERED)</Label>
                      <Input placeholder="Reason for non-delivery" value={form.exception_reason} onChange={e => setForm(p => ({...p, exception_reason: e.target.value}))}
                        required className="mt-1 h-8 text-xs border-red-200" data-testid={`delivery-exception-${item.id}`} />
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleSave(item.id)} disabled={saving} data-testid={`delivery-save-${item.id}`}>{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Verified info */}
            {item.verification_status !== "PENDING" && item.verified_by && (
              <div className="mt-2 text-xs text-slate-400">
                Verified by {item.verified_by} · {fmt(item.verified_at)}
                {item.delivery_note && <span className="ml-2 text-slate-500">"{item.delivery_note}"</span>}
                {item.exception_reason && <span className="ml-2 text-amber-600">Exception: {item.exception_reason}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Partners Tab ──────────────────────────────────────────────────────────────
function PartnersTab({ eid, canSupplier }) {
  const [reviews, setReviews] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [form, setForm] = useState({ supplier_id: "", partner_assignment_id: "", partner_name: "", punctuality_rating: 3, quality_rating: 3, responsiveness_rating: 3, scope_compliance_rating: 3, professionalism_rating: 3, overall_rating: 3, strengths: "", concerns: "", would_use_again: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pr, pa] = await Promise.all([closeoutApi.listSupplierReviews(eid), eventApi.listPartners(eid)]);
      setReviews(pr.data || []);
      setPartners(pa.data || []);
    } catch { toast.error("Failed to load partner data"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const reviewById = Object.fromEntries(reviews.map(r => [r.partner_assignment_id, r]));

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const existing = reviewById[showForm];
      if (existing) {
        await closeoutApi.updateSupplierReview(eid, existing.id, { ...form, review_status: "SUBMITTED" });
      } else {
        await closeoutApi.createSupplierReview(eid, form);
        const created = await closeoutApi.listSupplierReviews(eid);
        const newRev = created.data.find(r => r.partner_assignment_id === showForm);
        if (newRev) await closeoutApi.updateSupplierReview(eid, newRev.id, { review_status: "SUBMITTED" });
      }
      toast.success("Supplier review submitted");
      setShowForm(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const RatingInput = ({ label, field }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-1 mt-1">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setForm(p => ({...p, [field]: n}))}
            className={`w-8 h-8 rounded text-sm font-bold border transition-colors ${form[field] >= n ? "bg-amber-400 border-amber-500 text-white" : "bg-slate-50 border-slate-200 text-slate-400 hover:bg-amber-50"}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-slate-800">Partner Performance Reviews</h3>
      <p className="text-xs text-slate-400 italic">Operational performance observations only — no financial or commercial information.</p>

      {partners.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No partner assignments found for this event.</p>}

      <div className="space-y-3">
        {partners.map(partner => {
          const rev = reviewById[partner.id];
          return (
            <div key={partner.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`partner-review-row-${partner.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-medium text-slate-800">{partner.partner_name || partner.id}</p>
                  {partner.responsibility && <p className="text-xs text-slate-500">{partner.responsibility}</p>}
                </div>
                <div className="flex items-center gap-3">
                  {rev ? (
                    <>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${rev.review_status === "SUBMITTED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{rev.review_status}</span>
                      <div className="flex items-center gap-1"><Stars value={rev.overall_rating} /><span className="text-xs text-slate-500 ml-1">{rev.overall_rating}/5</span></div>
                      {rev.would_use_again !== null && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${rev.would_use_again ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>{rev.would_use_again ? "Use again" : "Would not reuse"}</span>
                      )}
                    </>
                  ) : <span className="text-xs text-slate-400">Not reviewed</span>}
                  {canSupplier && (
                    <Button size="sm" variant="outline" onClick={() => {
                      setShowForm(partner.id);
                      setForm(f => ({
                        ...f,
                        partner_assignment_id: partner.id,
                        partner_name: partner.partner_name || partner.id,
                        supplier_id: partner.supplier_id || partner.id,
                        ...(rev ? {
                          punctuality_rating: rev.punctuality_rating,
                          quality_rating: rev.quality_rating,
                          responsiveness_rating: rev.responsiveness_rating,
                          scope_compliance_rating: rev.scope_compliance_rating,
                          professionalism_rating: rev.professionalism_rating,
                          overall_rating: rev.overall_rating,
                          strengths: rev.strengths || "",
                          concerns: rev.concerns || "",
                          would_use_again: rev.would_use_again,
                        } : {}),
                      }));
                    }} data-testid={`review-partner-btn-${partner.id}`}>
                      {rev ? "Edit Review" : "Add Review"}
                    </Button>
                  )}
                </div>
              </div>

              {showForm === partner.id && (
                <form onSubmit={handleSubmitReview} className="mt-5 border-t border-slate-100 pt-5 space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <RatingInput label="Punctuality" field="punctuality_rating" />
                    <RatingInput label="Quality" field="quality_rating" />
                    <RatingInput label="Responsiveness" field="responsiveness_rating" />
                    <RatingInput label="Scope Compliance" field="scope_compliance_rating" />
                    <RatingInput label="Professionalism" field="professionalism_rating" />
                    <RatingInput label="Overall Rating" field="overall_rating" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Strengths</Label>
                      <Textarea rows={2} value={form.strengths} onChange={e => setForm(p => ({...p, strengths: e.target.value}))} className="mt-1 text-sm" data-testid="review-strengths" />
                    </div>
                    <div>
                      <Label className="text-xs">Concerns</Label>
                      <Textarea rows={2} value={form.concerns} onChange={e => setForm(p => ({...p, concerns: e.target.value}))} className="mt-1 text-sm" data-testid="review-concerns" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Label className="text-xs">Would Use Again?</Label>
                    {[{ v: true, l: "Yes" }, { v: false, l: "No" }, { v: null, l: "Undecided" }].map(({ v, l }) => (
                      <button key={l} type="button" onClick={() => setForm(p => ({...p, would_use_again: v}))}
                        className={`text-xs px-3 py-1 rounded border transition-colors ${form.would_use_again === v ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`would-use-${l.toLowerCase()}`}>{l}</button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={saving} data-testid="review-submit-btn">{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Submit Review</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowForm(null)}>Cancel</Button>
                  </div>
                </form>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Streams Tab ───────────────────────────────────────────────────────────────
function StreamsTab({ eid, canStream }) {
  const [reviews, setReviews] = useState([]);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [form, setForm] = useState({ execution_stream_id: "", stream_name: "", outcome_status: "SUCCESSFUL", planned_vs_actual_note: "", blockers_encountered: "", what_worked: "", what_did_not_work: "", follow_up_required: false, follow_up_note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sr, st] = await Promise.all([closeoutApi.listStreamReviews(eid), eventApi.listStreams(eid)]);
      setReviews(sr.data || []);
      setStreams(st.data || []);
    } catch { toast.error("Failed to load stream data"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const reviewByStream = Object.fromEntries(reviews.map(r => [r.execution_stream_id, r]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const existing = reviewByStream[showForm];
      if (existing) {
        await closeoutApi.updateStreamReview(eid, existing.id, { ...form, review_status: "SUBMITTED" });
      } else {
        const created = await closeoutApi.createStreamReview(eid, form);
        await closeoutApi.updateStreamReview(eid, created.data.id, { review_status: "SUBMITTED" });
      }
      toast.success("Stream review submitted");
      setShowForm(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-slate-800">Team & Stream Review</h3>
      <p className="text-xs text-slate-400 italic">Operational outcome only — not employee performance appraisal.</p>
      {streams.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No execution streams found for this event.</p>}
      <div className="space-y-3">
        {streams.map(stream => {
          const rev = reviewByStream[stream.id];
          return (
            <div key={stream.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`stream-review-row-${stream.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-medium text-slate-800">{stream.name}</p>
                  {stream.lead_user_id && <p className="text-xs text-slate-500">Lead: {stream.lead_user_id}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {rev ? (
                    <>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STREAM_OUTCOME_STYLE[rev.outcome_status] || ""}`}>{rev.outcome_status?.replace(/_/g, " ")}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${rev.review_status === "SUBMITTED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{rev.review_status}</span>
                    </>
                  ) : <span className="text-xs text-slate-400">Not reviewed</span>}
                  {canStream && (
                    <Button size="sm" variant="outline" onClick={() => {
                      setShowForm(stream.id);
                      setForm(f => ({
                        ...f,
                        execution_stream_id: stream.id,
                        stream_name: stream.name,
                        ...(rev ? {
                          outcome_status: rev.outcome_status,
                          planned_vs_actual_note: rev.planned_vs_actual_note || "",
                          blockers_encountered: rev.blockers_encountered || "",
                          what_worked: rev.what_worked || "",
                          what_did_not_work: rev.what_did_not_work || "",
                          follow_up_required: rev.follow_up_required || false,
                          follow_up_note: rev.follow_up_note || "",
                        } : {}),
                      }));
                    }} data-testid={`review-stream-btn-${stream.id}`}>{rev ? "Edit Review" : "Review Stream"}</Button>
                  )}
                </div>
              </div>
              {rev && rev.blockers_encountered && (
                <p className="text-xs text-slate-500 mt-2">Blockers: {rev.blockers_encountered}</p>
              )}
              {rev && rev.follow_up_required && (
                <p className="text-xs text-amber-600 mt-1">Follow-up required: {rev.follow_up_note || "Yes"}</p>
              )}

              {showForm === stream.id && (
                <form onSubmit={handleSubmit} className="mt-5 border-t border-slate-100 pt-5 space-y-3">
                  <div>
                    <Label className="text-xs">Operational Outcome *</Label>
                    <select value={form.outcome_status} onChange={e => setForm(p => ({...p, outcome_status: e.target.value}))}
                      className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="stream-outcome-select">
                      {["SUCCESSFUL","SUCCESSFUL_WITH_ISSUES","PARTIAL","FAILED","NOT_APPLICABLE"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Planned vs Actual</Label>
                      <Textarea rows={2} value={form.planned_vs_actual_note} onChange={e => setForm(p => ({...p, planned_vs_actual_note: e.target.value}))} className="mt-1 text-sm" data-testid="stream-plan-actual" />
                    </div>
                    <div>
                      <Label className="text-xs">Blockers Encountered</Label>
                      <Textarea rows={2} value={form.blockers_encountered} onChange={e => setForm(p => ({...p, blockers_encountered: e.target.value}))} className="mt-1 text-sm" data-testid="stream-blockers" />
                    </div>
                    <div>
                      <Label className="text-xs">What Worked</Label>
                      <Textarea rows={2} value={form.what_worked} onChange={e => setForm(p => ({...p, what_worked: e.target.value}))} className="mt-1 text-sm" data-testid="stream-worked" />
                    </div>
                    <div>
                      <Label className="text-xs">What Did Not Work</Label>
                      <Textarea rows={2} value={form.what_did_not_work} onChange={e => setForm(p => ({...p, what_did_not_work: e.target.value}))} className="mt-1 text-sm" data-testid="stream-not-worked" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={form.follow_up_required} onChange={e => setForm(p => ({...p, follow_up_required: e.target.checked}))} id="follow-up" className="accent-indigo-600" data-testid="stream-followup-checkbox" />
                    <label htmlFor="follow-up" className="text-xs text-slate-700 cursor-pointer">Follow-up Required</label>
                    {form.follow_up_required && (
                      <Input placeholder="Follow-up note" value={form.follow_up_note} onChange={e => setForm(p => ({...p, follow_up_note: e.target.value}))} className="h-8 text-xs flex-1 ml-2" data-testid="stream-followup-note" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={saving} data-testid="stream-review-submit">{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Submit Review</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowForm(null)}>Cancel</Button>
                  </div>
                </form>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Client Feedback Tab ───────────────────────────────────────────────────────
function FeedbackTab({ eid, canFeedback }) {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ status: "NOT_REQUESTED", feedback_source: "", overall_rating: 0, planning_experience_rating: 0, design_experience_rating: 0, execution_rating: 0, communication_rating: 0, comments: "", testimonial_permission: false, follow_up_requested: false, received_at: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await closeoutApi.getFeedback(eid); setFeedback(r.data); }
    catch { toast.error("Failed to load feedback"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    if (payload.overall_rating === 0) delete payload.overall_rating;
    if (payload.planning_experience_rating === 0) delete payload.planning_experience_rating;
    if (payload.design_experience_rating === 0) delete payload.design_experience_rating;
    if (payload.execution_rating === 0) delete payload.execution_rating;
    if (payload.communication_rating === 0) delete payload.communication_rating;
    try {
      if (feedback) {
        await closeoutApi.updateFeedback(eid, feedback.id, payload);
      } else {
        await closeoutApi.createFeedback(eid, payload);
      }
      toast.success("Feedback saved");
      setEditing(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openEdit = () => {
    setEditing(true);
    if (feedback) {
      setForm({
        status: feedback.status || "NOT_REQUESTED",
        feedback_source: feedback.feedback_source || "",
        overall_rating: feedback.overall_rating || 0,
        planning_experience_rating: feedback.planning_experience_rating || 0,
        design_experience_rating: feedback.design_experience_rating || 0,
        execution_rating: feedback.execution_rating || 0,
        communication_rating: feedback.communication_rating || 0,
        comments: feedback.comments || "",
        testimonial_permission: feedback.testimonial_permission || false,
        follow_up_requested: feedback.follow_up_requested || false,
        received_at: "",
      });
    }
  };

  const RatingRow = ({ label, field }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-700 w-48">{label}</span>
      <div className="flex gap-1">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setForm(p => ({...p, [field]: n}))}
            className={`w-7 h-7 rounded text-xs font-bold border transition-colors ${form[field] >= n ? "bg-amber-400 border-amber-500 text-white" : "bg-slate-50 border-slate-200 text-slate-400"}`}>{n}</button>
        ))}
      </div>
    </div>
  );

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Client Feedback</h3>
        {canFeedback && !editing && (
          <Button size="sm" variant="outline" onClick={openEdit} data-testid="add-feedback-btn">
            <Pencil className="w-3.5 h-3.5 mr-1" />{feedback ? "Edit Feedback" : "Record Feedback"}
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-400 italic">Only record stated client feedback — no AI sentiment inference.</p>

      {!editing && !feedback && (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
          <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">No client feedback recorded yet.</p>
        </div>
      )}

      {!editing && feedback && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="feedback-display">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${feedback.status === "RECEIVED" ? "bg-emerald-100 text-emerald-700" : feedback.status === "DECLINED" ? "bg-red-100 text-red-600" : feedback.status === "REQUESTED" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{feedback.status}</span>
            {feedback.feedback_source && <span className="text-xs text-slate-500">Source: {feedback.feedback_source}</span>}
          </div>
          {feedback.overall_rating && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Overall:</span>
              <Stars value={feedback.overall_rating} />
              <span className="text-sm font-semibold text-slate-800">{feedback.overall_rating}/5</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
            {feedback.planning_experience_rating && <div>Planning: <Stars value={feedback.planning_experience_rating} /></div>}
            {feedback.design_experience_rating && <div>Design: <Stars value={feedback.design_experience_rating} /></div>}
            {feedback.execution_rating && <div>Execution: <Stars value={feedback.execution_rating} /></div>}
            {feedback.communication_rating && <div>Communication: <Stars value={feedback.communication_rating} /></div>}
          </div>
          {feedback.comments && <p className="text-sm text-slate-700 italic border-l-4 border-slate-200 pl-3">"{feedback.comments}"</p>}
          <div className="flex gap-4 text-xs text-slate-500">
            {feedback.testimonial_permission && <span className="text-emerald-600">Testimonial permission granted</span>}
            {feedback.follow_up_requested && <span className="text-amber-600">Follow-up requested</span>}
          </div>
        </div>
      )}

      {editing && (
        <form onSubmit={handleSave} className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Status *</Label>
              <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))}
                className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="feedback-status-select">
                {["NOT_REQUESTED","REQUESTED","RECEIVED","DECLINED"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <select value={form.feedback_source} onChange={e => setForm(p => ({...p, feedback_source: e.target.value}))}
                className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="feedback-source-select">
                <option value="">Select source</option>
                {["DIRECT","SURVEY","PHONE","EMAIL","WHATSAPP","OTHER"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {form.status === "RECEIVED" && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700">Ratings (1 = Poor, 5 = Excellent)</h4>
              <RatingRow label="Overall Experience" field="overall_rating" />
              <RatingRow label="Planning Experience" field="planning_experience_rating" />
              <RatingRow label="Design Experience" field="design_experience_rating" />
              <RatingRow label="Event Execution" field="execution_rating" />
              <RatingRow label="Communication" field="communication_rating" />
              <div>
                <Label className="text-xs">Client Comments</Label>
                <Textarea placeholder="Exact client words" value={form.comments} onChange={e => setForm(p => ({...p, comments: e.target.value}))} className="mt-1 text-sm" rows={3} data-testid="feedback-comments" />
              </div>
            </div>
          )}

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.testimonial_permission} onChange={e => setForm(p => ({...p, testimonial_permission: e.target.checked}))} className="accent-indigo-600" data-testid="feedback-testimonial" />
              Testimonial permission
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.follow_up_requested} onChange={e => setForm(p => ({...p, follow_up_requested: e.target.checked}))} className="accent-indigo-600" data-testid="feedback-followup" />
              Follow-up requested
            </label>
          </div>

          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid="feedback-save-btn">{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save Feedback</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Lessons Tab ───────────────────────────────────────────────────────────────
function LessonsTab({ eid, canLesson }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ category: "OPERATIONS", title: "", observation: "", impact: "MEDIUM", lesson: "", future_consideration: "", source_type: "MANUAL" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try { const r = await closeoutApi.listLessons(eid); setLessons(r.data); }
    catch { toast.error("Failed to load lessons"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await closeoutApi.createLesson(eid, form);
      toast.success("Lesson captured");
      setShowCreate(false);
      setForm({ category: "OPERATIONS", title: "", observation: "", impact: "MEDIUM", lesson: "", future_consideration: "", source_type: "MANUAL" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (lid) => {
    if (!window.confirm("Delete this lesson?")) return;
    setDeleting(lid);
    try { await closeoutApi.deleteLesson(eid, lid); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setDeleting(null); }
  };

  const byCategory = lessons.reduce((acc, l) => { (acc[l.category] = acc[l.category] || []).push(l); return acc; }, {});

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold text-slate-800">Lessons Learned ({lessons.length})</h3>
          <p className="text-xs text-slate-400 italic mt-0.5">Human-authored only — no AI generation.</p>
        </div>
        {canLesson && (
          <Button size="sm" variant="outline" onClick={() => setShowCreate(v => !v)} data-testid="add-lesson-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Capture Lesson
          </Button>
        )}
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-blue-100 p-5 space-y-3">
          <h4 className="font-medium text-slate-700 text-sm">New Lesson Learned</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category *</Label>
              <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))}
                className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="lesson-category-select">
                {LESSON_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Impact</Label>
              <select value={form.impact} onChange={e => setForm(p => ({...p, impact: e.target.value}))}
                className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="lesson-impact-select">
                {["LOW","MEDIUM","HIGH"].map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input placeholder="Brief lesson title" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="lesson-title-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Observation *</Label>
              <Textarea placeholder="What was observed?" value={form.observation} onChange={e => setForm(p => ({...p, observation: e.target.value}))} required rows={2} className="mt-1 text-sm" data-testid="lesson-observation-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Lesson *</Label>
              <Textarea placeholder="What is the lesson?" value={form.lesson} onChange={e => setForm(p => ({...p, lesson: e.target.value}))} required rows={2} className="mt-1 text-sm" data-testid="lesson-lesson-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Future Consideration</Label>
              <Input placeholder="What to do differently next time" value={form.future_consideration} onChange={e => setForm(p => ({...p, future_consideration: e.target.value}))} className="mt-1 h-8 text-sm" data-testid="lesson-future-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid="lesson-create-submit">{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Capture Lesson</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      {lessons.length === 0 && !showCreate && (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
          <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">No lessons captured yet. {canLesson ? "Add your first lesson above." : ""}</p>
        </div>
      )}

      {Object.entries(byCategory).map(([cat, catLessons]) => (
        <div key={cat}>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{cat}</h4>
          <div className="space-y-2">
            {catLessons.map(l => (
              <div key={l.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`lesson-row-${l.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-800 text-sm">{l.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${IMPACT_STYLE[l.impact] || ""}`}>{l.impact}</span>
                    </div>
                    <p className="text-xs text-slate-600">{l.observation}</p>
                    <p className="text-xs text-indigo-700 mt-1 font-medium">Lesson: {l.lesson}</p>
                    {l.future_consideration && <p className="text-xs text-emerald-600 mt-0.5">Future: {l.future_consideration}</p>}
                  </div>
                  {canLesson && (
                    <button onClick={() => handleDelete(l.id)} disabled={deleting === l.id}
                      className="text-slate-300 hover:text-red-400 transition-colors" data-testid={`delete-lesson-${l.id}`}>
                      {deleting === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
